import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, FileDown, Printer } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";
import { SessionPrintableSheet } from "./SessionPrintableSheet";

interface ClosedSession {
  id: string;
  module_session_id: string | null;
  module_code: string | null;
  class: string | null;
  topic: string | null;
  session_date: string;
  opened_at: string | null;
  closed_at: string | null;
  status: string;
}

interface ModuleSessionRow {
  id: string;
  semester: string;
  class: string;
  module_code: string;
  module_name: string | null;
  session_no: number;
  session_name: string;
  session_kind: string;
}

interface SummaryRow {
  session_id: string;
  total_enrolled: number | null;
  present_count: number | null;
  pending_count: number | null;
  absent_count: number | null;
  cr_recorded_count: number | null;
  attendance_rate: number | null;
  generated_at: string | null;
  generated_by: string | null;
}

interface RosterRow {
  id: string;
  status: string;
  scanned_at: string | null;
  scanned_by: string | null;
  student_id: string;
  student_name: string;
  reg_no: string;
  scanned_by_name?: string;
}

function formatDuration(openedAt: string | null, closedAt: string | null) {
  if (!openedAt || !closedAt) return "—";
  const diffMs = new Date(closedAt).getTime() - new Date(openedAt).getTime();
  if (diffMs <= 0) return "—";
  const mins = Math.floor(diffMs / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

interface Props {
  session: ClosedSession | null;
  moduleSession: ModuleSessionRow | null;
  summary: SummaryRow | null;
  onClose: () => void;
}

export function SessionHistoryDetailDialog({ session, moduleSession, summary, onClose }: Props) {
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [closedByName, setClosedByName] = useState<string>("");
  const [generatedByName, setGeneratedByName] = useState<string>("");

  useEffect(() => {
    if (!session) {
      setRoster([]);
      setClosedByName("");
      setGeneratedByName("");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);

      // Roster: attendance_records joined to students
      const { data: recs } = await supabase
        .from("attendance_records")
        .select("id, status, scanned_at, scanned_by, student_id, students!inner(first_name, last_name, student_number)")
        .eq("session_id", session.id)
        .eq("attendance_type", "session");

      const rows: RosterRow[] = ((recs ?? []) as any[]).map((r) => ({
        id: r.id,
        status: r.status,
        scanned_at: r.scanned_at,
        scanned_by: r.scanned_by,
        student_id: r.student_id,
        student_name: `${r.students?.first_name ?? ""} ${r.students?.last_name ?? ""}`.trim(),
        reg_no: r.students?.student_number ?? "",
      }));

      // Resolve scanned_by names
      const scannerIds = Array.from(new Set(rows.map((r) => r.scanned_by).filter(Boolean))) as string[];
      let scannerMap: Record<string, string> = {};
      if (scannerIds.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, full_name")
          .in("user_id", scannerIds);
        (profs ?? []).forEach((p: any) => { scannerMap[p.user_id] = p.full_name ?? ""; });
      }
      rows.forEach((r) => { if (r.scanned_by) r.scanned_by_name = scannerMap[r.scanned_by] ?? ""; });

      // Closed by — from audit_logs (action='session_closed')
      const { data: closeLog } = await supabase
        .from("audit_logs")
        .select("user_id, created_at")
        .eq("table_name", "attendance_sessions")
        .eq("record_id", session.id)
        .order("created_at", { ascending: false })
        .limit(50);
      const closedEntry = (closeLog ?? []).find((l: any) => {
        return true; // any update around close-time; we'll resolve by user
      });

      const userIdsToResolve: string[] = [];
      if (closedEntry?.user_id) userIdsToResolve.push(closedEntry.user_id);
      if (summary?.generated_by) userIdsToResolve.push(summary.generated_by);
      if (userIdsToResolve.length) {
        const { data: pp } = await supabase
          .from("profiles")
          .select("user_id, full_name")
          .in("user_id", userIdsToResolve);
        const m: Record<string, string> = {};
        (pp ?? []).forEach((p: any) => { m[p.user_id] = p.full_name ?? ""; });
        if (closedEntry?.user_id) setClosedByName(m[closedEntry.user_id] ?? "");
        if (summary?.generated_by) setGeneratedByName(m[summary.generated_by] ?? "");
      }

      if (!cancelled) {
        setRoster(rows);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session, summary]);

  if (!session) return null;

  const headerLabel = moduleSession
    ? `${moduleSession.class} · Sem ${moduleSession.semester} · ${moduleSession.module_code}${moduleSession.module_name ? ` — ${moduleSession.module_name}` : ""}`
    : `${session.module_code ?? "—"} · ${session.class ?? "—"}`;
  const subLabel = moduleSession
    ? `Session #${moduleSession.session_no} ${moduleSession.session_name}`
    : (session.topic ?? "Uncatalogued session");

  function exportRosterCsv() {
    const rows = roster.map((r) => [
      r.reg_no,
      r.student_name,
      r.status,
      r.scanned_at ? new Date(r.scanned_at).toLocaleString() : "",
      r.scanned_by_name ?? "",
    ]);
    exportToCsv(
      `Roster_${session!.session_date}_${session!.module_code ?? "session"}.csv`,
      ["Reg No", "Student", "Status", "Scanned At", "Scanned By"],
      rows,
    );
  }

  function statusVariant(s: string): "default" | "secondary" | "destructive" | "outline" {
    if (s === "present") return "default";
    if (s === "rejected") return "destructive";
    if (s.startsWith("pending")) return "secondary";
    return "outline";
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-base">{headerLabel}</DialogTitle>
          <p className="text-sm text-muted-foreground">{subLabel}</p>
        </DialogHeader>

        {/* Timing */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Date</p>
            <p className="font-medium">{format(new Date(session.session_date + "T00:00:00"), "dd MMM yyyy")}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Opened</p>
            <p className="font-medium">{session.opened_at ? format(new Date(session.opened_at), "HH:mm") : "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Closed</p>
            <p className="font-medium">{session.closed_at ? format(new Date(session.closed_at), "HH:mm") : "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Duration</p>
            <p className="font-medium">{formatDuration(session.opened_at, session.closed_at)}</p>
          </div>
        </div>

        {(closedByName || generatedByName) && (
          <p className="text-xs text-muted-foreground">
            {closedByName && <>Closed by <span className="font-medium text-foreground">{closedByName}</span>. </>}
            {generatedByName && summary?.generated_at && (
              <>Summary generated by <span className="font-medium text-foreground">{generatedByName}</span> on {new Date(summary.generated_at).toLocaleString()}.</>
            )}
          </p>
        )}

        {/* Summary chips */}
        {summary && (
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">Enrolled: {summary.total_enrolled ?? 0}</Badge>
            <Badge variant="default">Present: {summary.present_count ?? 0}</Badge>
            <Badge variant="secondary">Pending: {summary.pending_count ?? 0}</Badge>
            <Badge variant="outline">Absent: {summary.absent_count ?? 0}</Badge>
            <Badge variant="outline">CR-recorded: {summary.cr_recorded_count ?? 0}</Badge>
            <Badge variant="outline">
              Rate: {summary.attendance_rate != null ? `${(Number(summary.attendance_rate) * 100).toFixed(1)}%` : "—"}
            </Badge>
          </div>
        )}

        {/* Roster */}
        <div className="border rounded-md overflow-x-auto max-h-[40vh] overflow-y-auto">
          {loading ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading roster…</div>
          ) : roster.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">No attendance records for this session.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reg No</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Scanned at</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roster.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs whitespace-nowrap">{r.reg_no}</TableCell>
                    <TableCell className="text-sm">{r.student_name}</TableCell>
                    <TableCell><Badge variant={statusVariant(r.status)}>{r.status}</Badge></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{r.scanned_at ? new Date(r.scanned_at).toLocaleString() : "—"}</TableCell>
                    <TableCell className="text-xs">{r.scanned_by_name ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={exportRosterCsv} disabled={!roster.length}>
            <FileDown className="w-4 h-4 mr-1" /> Export roster CSV
          </Button>
          <SessionPrintableSheet session={{
            id: session.id,
            topic: session.topic,
            module_code: session.module_code,
            class: session.class,
            session_date: session.session_date,
          }} />
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
